import { GridPaginationModel, GridRowParams, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'src/hooks/use-debounce';
import { useTableFiltersPersistContext } from 'src/providers/table-filters-persist.provider';
import { paths } from 'src/routes/paths';
import { useHomeBannersStore } from 'src/store/personalization/home-banners/home-banners.store';
import { homeBannerItem } from 'src/types/home-banners';
import { enqueueSnackbar } from 'src/components/snackbar';
import { getHomeBannersColumns } from './components/home-banners-columns';
import { useBoolean } from 'src/hooks/use-boolean';

export type HomeBannerRow = homeBannerItem & { id: string };

export type HomeBannerFilters = {
  status: '' | homeBannerItem['status'];
  type: '' | homeBannerItem['type'];
  tier: string;
  priority: string;
};

const STATUS_CHANGE_DEBOUNCE_MS = 700;

export const useHomeBanners = () => {
  const {
    isLoading,
    homeBanners,
    selectedBanners,
    findAll,
    changeHomeBannerStatus,
    setSelectedBanners,
    deleteSelectedBanners,
  } = useHomeBannersStore();
  const navigate = useNavigate();

  const { updateFilters, getFilters } = useTableFiltersPersistContext();

  const searchParamsFilters = getFilters<{
    debouncedSearchText?: string;
    filters?: HomeBannerFilters;
    sortModel?: GridSortModel;
    paginationModel?: GridPaginationModel;
  }>([
    paths.dashboard.homeBannerDetails(':id'),
    paths.dashboard.homeBannerCreate,
  ]);

  const [searchText, setSearchText] = useState(searchParamsFilters?.debouncedSearchText || '');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [filters, setFilters] = useState<HomeBannerFilters>(searchParamsFilters?.filters || {
    status: '',
    type: '',
    tier: '',
    priority: '',
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(searchParamsFilters?.sortModel || [{
    field: 'status',
    sort: 'asc',
  }]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);
  const [statusByBannerId, setStatusByBannerId] = useState<Record<string, 'active' | 'inactive'>>({});
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>(selectedBanners || []);
  const statusChangeTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const statusBeforeChangeRef = useRef<Record<string, string>>({});
  const showDeleteButton = useBoolean(false);
  const showConfirmDeleteDialog = useBoolean(false);

  useEffect(() => {
    findAll();
  }, [findAll]);

  const filteredBanners = useMemo(() => {
    const search = debouncedSearchText.trim().toLowerCase();
    const filtered = homeBanners.filter((banner) => {
      const searchableText = [banner.name, banner.type, banner.status, ...(banner.visibleForTiers || [])]
        .join(' ')
        .toLowerCase();
      const matchesSearch = !search || searchableText.includes(search);
      const matchesStatus = !filters.status || banner.status === filters.status;
      const matchesType = !filters.type || banner.type === filters.type;
      const matchesTier = !filters.tier || banner.visibleForTiers?.includes(filters.tier) || (filters.tier === 'all' && banner.visibleForTiers?.length === 0);
      const matchesPriority = !filters.priority || banner.priority === Number(filters.priority);

      return matchesSearch && matchesStatus && matchesType && matchesTier && matchesPriority;
    });

    const sort = sortModel[0];
    const sorted = sort ? [...filtered].sort((first, second) => {
      const firstValue = first[sort.field as keyof homeBannerItem];
      const secondValue = second[sort.field as keyof homeBannerItem];
      const comparison = String(firstValue ?? '').localeCompare(String(secondValue ?? ''), 'pt-BR', {
        numeric: true,
        sensitivity: 'base',
      });
      return sort.sort === 'desc' ? -comparison : comparison;
    }) : filtered;

    return sorted.map((banner) => ({
      ...banner,
      status: statusByBannerId[banner._id || ''] || banner.status,
    }));
  }, [debouncedSearchText, filters, homeBanners, sortModel, statusByBannerId]);

  const homeBannersRows = useMemo<HomeBannerRow[]>(
    () => filteredBanners.slice(
      paginationModel.page * paginationModel.pageSize,
      (paginationModel.page + 1) * paginationModel.pageSize
    ).map((banner, index) => ({ ...banner, id: banner._id || `${paginationModel.page}-${index}-${banner.name}` })),
    [filteredBanners, paginationModel]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredBanners.length / paginationModel.pageSize) - 1);
    if (paginationModel.page > maxPage) {
      setPaginationModel((current) => ({ ...current, page: maxPage }));
    }

    updateFilters(JSON.stringify({ debouncedSearchText, filters, sortModel, paginationModel }));
  }, [debouncedSearchText, filters, filteredBanners.length, paginationModel, sortModel, updateFilters]);

  const handleSortModelChange = useCallback((model: GridSortModel) => setSortModel(model), []);

  const handleRowClick = useCallback((params: GridRowParams<HomeBannerRow>) => {
    navigate(paths.dashboard.homeBannerDetails(String(params.row._id || params.id)));
  }, [navigate]);

  const onRowSelectionModelChange = useCallback((model: GridRowSelectionModel) => {
    setSelectedRows(model)
    setSelectedBanners(model);
  }, []);

  const onHeaderSelectionCheckboxChange = useCallback((checked: boolean) => {
    setSelectedRows(checked ? filteredBanners.map((row) => row._id) : []);
    setSelectedBanners(checked ? filteredBanners.map((row) => row._id) : []);
  }, [filteredBanners]);

  const selectedFiltersCount = Object.values(filters).filter(Boolean).length;

  const setFilter = useCallback(<K extends keyof HomeBannerFilters>(key: K, value: HomeBannerFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ status: '', type: '', tier: '', priority: '' });
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const handleFilterButtonClick = useCallback(() => setOpenFiltersDrawer(true), []);
  const onCloseFilters = useCallback(() => setOpenFiltersDrawer(false), []);

  const handleChangeStatus = useCallback(
    (bannerId: string, rawStatus: 'active' | 'inactive') => {
      const previousStatus = homeBannersRows.find(
        (row) => String(row._id) === bannerId
      )?.status;

      if (!Object.prototype.hasOwnProperty.call(statusBeforeChangeRef.current, bannerId)) {
        statusBeforeChangeRef.current[bannerId] = previousStatus || '';
      }

      const statusBeforeChange = statusBeforeChangeRef.current[bannerId];

      setStatusByBannerId((current) => {
        const next = { ...current };

        if (rawStatus === statusBeforeChange) {
          delete next[bannerId];
          return next;
        }

        next[bannerId] = rawStatus;
        return next;
      });

      if (statusChangeTimersRef.current[bannerId]) {
        clearTimeout(statusChangeTimersRef.current[bannerId]);
      }

      if (rawStatus === statusBeforeChange) {
        delete statusBeforeChangeRef.current[bannerId];
        delete statusChangeTimersRef.current[bannerId];
        return;
      }

      statusChangeTimersRef.current[bannerId] = setTimeout(async () => {
        const rollbackStatus = statusBeforeChangeRef.current[bannerId];

        delete statusChangeTimersRef.current[bannerId];

        try {
          await changeHomeBannerStatus(bannerId, rawStatus);
          delete statusBeforeChangeRef.current[bannerId];
          enqueueSnackbar('Status do banner atualizado com sucesso!', { variant: 'success' });
        } catch (error) {
          setStatusByBannerId((current) => {
            const next = { ...current };

            if (rollbackStatus === 'active' || rollbackStatus === 'inactive') {
              next[bannerId] = rollbackStatus;
              return next;
            }

            delete next[bannerId];
            return next;
          });

          delete statusBeforeChangeRef.current[bannerId];
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      }, STATUS_CHANGE_DEBOUNCE_MS);
    },
    [homeBannersRows, changeHomeBannerStatus, enqueueSnackbar]
  );

  const homeBannersColumns = getHomeBannersColumns({
    handleChangeStatus,
  });

  useEffect(() => {
    if (selectedRows.length > 0) {
      showDeleteButton.onTrue();
    } else {
      showDeleteButton.onFalse();
    }
  }, [selectedRows, showDeleteButton]);

  const handleConfirmDelete = async () => {
    try {
      await deleteSelectedBanners();
      onRowSelectionModelChange([]);
      await findAll();
      enqueueSnackbar('Banners deletados com sucesso!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return {
    isLoading,
    homeBanners,
    homeBannersRows,
    homeBannersTotal: filteredBanners.length,
    gridSortModel: sortModel,
    paginationModel,
    selectedRows,
    searchText,
    selectedFiltersCount,
    filters,
    homeBannersColumns,
    openFiltersDrawer,
    showDeleteButton,
    showConfirmDeleteDialog,
    handleConfirmDelete,
    navigate,
    setPaginationModel,
    handleSortModelChange,
    handleRowClick,
    onRowSelectionModelChange,
    onHeaderSelectionCheckboxChange,
    setSearchText,
    setFilter,
    clearFilters,
    handleFilterButtonClick,
    onCloseFilters,
  };
};
